import React from 'react';
import _ from "lodash";
import moment from 'moment';

import { HistoricalValueChart } from 'fincite-react-charts';
import {handleChartSizeOnWindowResize} from "../../../../../../utils/utils";
import ChartLegend from '../../../../../../components/Charts/Legend';
import {BENCHMARK_CHART_COLOR, CHART_RESPONSIVE_RULES, DATE_FORMAT} from "../../../../../../utils/constants";
import {toShortGermanFormat} from "../../../../../../utils/numberFormater";
import {ChartTooltipSettings} from "../../../../utils";
import {setTimeSelectorDateChange} from "../../../../actions";
import {renderToStaticMarkup} from "react-dom/server";


const getColorsFromChartData = (data) => data.map((portfolio) => portfolio.color)

const _performanceChartPropsAreEquals = (prevProps, nextProps) => {
  return prevProps.chartData.timestamp === nextProps.chartData.timestamp
    && prevProps.startDate === nextProps.startDate
    && prevProps.endDate === nextProps.endDate
}

function yAxisFormatter() {
  return toShortGermanFormat(this.value, '', ' %', 2, true);
}

export const _PerformanceLineChart = React.memo(({chartData, dispatch, classes, onDatesChange, startDate, endDate, withLegend=true}) => {
  const handleOnPointClick = (endDate) => {
    dispatch(setTimeSelectorDateChange({
      startOfBenchmarkChanged: true,
      start: moment(chartData.startOfBenchmark.date),
      end: moment(endDate)
    }));
  }

  const updatePoints = (series) => {
    series.points.forEach(p => {
      p.update({
        marker: {
          fillColor: '#FFFFFF',
          lineWidth: 2,
          width: 5,
          lineColor: BENCHMARK_CHART_COLOR,
          enabled: true,
          symbol: 'circle',
          radius: 5
        },
        events: {
          click: function () {
            handleOnPointClick(this.series.chart.rangeSelector.maxInput.value);
          },
          mouseOver: function () {
            this.dataLabel.show();
          },
          mouseOut: function () {
            this.dataLabel.hide();
          }
        }
      });
    });
  }

  const hideDataLabels = (series) => {
    series.points.forEach(p => {
      p.dataLabel.hide();
    });
  }

  const updateSeries = (series) => {
    series.update({
      dataGrouping: {
        enabled: true,
        dateTimeLabelFormats: {
          week: ["Woche vom %A, %b %e, %Y"],
        }
      }
    })
  }

  const data = _.isEmpty(chartData.data) ? [] : [
    ...chartData.data,
    chartData.showStartOfBenchmark ? {
      name: 'startOfBenchmark',
      portfolio: [chartData.startOfBenchmark]
    } : {name: '', portfolio: []}
  ]

  return (
    <div className={classes.chartContainer}>
      {withLegend && (
        <ChartLegend legendItems={chartData.data} />
      )}
      <HistoricalValueChart
        colors={getColorsFromChartData(data)}
        title=''
        data={data}
        chartOptions={{
          chart: {
            height: 350,
            marginTop: 0,
            events: {
              ...ChartTooltipSettings.chart.events,
              load: function (event) {
                this.tooltip.options.style.width = (this.chartWidth - 10) + 'px';
                handleChartSizeOnWindowResize(event);
                setTimeout(() => {
                  if (!this.series) {
                    return
                  }
                  (this.series.filter(s => s.name === 'startOfBenchmark') || []).forEach(s => {
                    updatePoints(s);
                  });
                  this.series.forEach(s => {
                    updateSeries(s);
                  });
                }, 100)
              },
              render: function() {
                if (!this.series) {
                  return
                }
                (this.series.filter(s => s.name === 'startOfBenchmark') || []).forEach(s => {
                  hideDataLabels(s);
                });
              }
            },
            animation: false,
          },
          boost: {
            enabled: true,
            useGPUTranslations: true,
            seriesThreshold: 1,
            debug: {
              timeRendering: true
            }
          },
          rangeSelector: {
            allButtonsEnabled: false,
            inputStyle: {
              visibility: 'hidden',
              opacity: 0,
            },
            labelStyle:{
              visibility: 'hidden',
              opacity: 0,
            },
            verticalAlign: 'top',
            buttons: []
          },
          navigator: {
            enabled: true
          },
          legend: {
            enabled: false,
          },
          scrollbar: {
            enabled: true
          },
          plotOptions: {
            series: {
              compareBase: 0,
              showInNavigator: true,
              dataGrouping: {
                enabled: false
              },
              dataLabels: {
                enabled: true,
                useHtml: true,
                allowOverlap: true,
                shape: 'callout',
                borderColor: '#666666',
                backgroundColor: 'rgba(255, 255, 255, .9)',
                borderWidth: 1,
                borderRadius: 6,
                y: -15,
                shadow: true,
                zIndex: 99,
                style: {
                  cursor: 'default',
                  fontWeight: 'normal'
                },
                formatter: function () {
                  if(this.series.name === 'startOfBenchmark'){
                    return renderToStaticMarkup(
                      <>
                        Startdatum der<br/>
                        Benchmark:<br/>
                        {chartData.startOfBenchmark && moment(chartData.startOfBenchmark.date).format(DATE_FORMAT)}
                      </>
                    );
                  }
                  return null;
                }
              },
            }
          },
          tooltip: {
            ...ChartTooltipSettings.tooltip,
            pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y} %</b><br/>',
            valueDecimals: 2,
            split: true,
            padding: ChartTooltipSettings.getTooltipPadding(data.length)
          },
          yAxis: [{
            opposite: false,
            align: 'right',
            labels: {
              enabled: true,
              x: -2,
              y: 4,
              formatter: yAxisFormatter
            }
          }, {
            linkedTo: 0,
            opposite: true,
            align: 'right',
            labels: {
              enabled: true,
              x: 2,
              y: 4,
              formatter: yAxisFormatter
            }
          }],
          xAxis: {
            min: Date.parse(startDate) || undefined,
            max: Date.parse(endDate) || undefined,
            gridLineWidth: 1,
            events: {
              setExtremes: function (event) {
                const self = event.target.chart;
                if(self.rangeSelector.minInput.value && self.rangeSelector.maxInput.value) {
                  onDatesChange({start: moment(self.rangeSelector.minInput.value), end: moment(self.rangeSelector.maxInput.value)});
                }
              }
            }
          },
          responsive: CHART_RESPONSIVE_RULES
        }}
      />
    </div>
  )
}, _performanceChartPropsAreEquals)